import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Flex, Form, Input } from 'antd';
import {
  StyledEmailVerificationContent,
  StyledEmailVerificationPage,
  StyledOTPWrapper,
} from './EmailVerification.styled';
import { ResendCodeCountdownMemoized } from './ResendCodeCountdown/ResendCodeCountDown';
import { ErrorMessage } from './ErrorMessage/ErrorMessage';
import { SuccessMessage } from './SuccessMessage/SuccessMessage';
import { MaxAttemptsMessage } from './MaxAttemptsMessage/MaxAttemptsMessage';
import { OTP_LENGTH, EXPECTED_OTP, MAX_OTP_ATTEMPTS } from 'app/modules/service/utils/constants';
import { useServiceRequestContext } from 'app/modules/service/context/ServiceContext';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';

export const EmailVerification = (): JSX.Element => {
  const { t } = useTranslation();

  const { handleServiceNext, handleServiceBack, emailRequested } = useServiceRequestContext();

  const [form] = Form.useForm();
  const [otpInputValue, setOtpInputValue] = useState('');
  const [otpRenderKey, setOtpRenderKey] = useState(0);
  const [resendResetSignal, setResendResetSignal] = useState(0);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lastCheckedOtp, setLastCheckedOtp] = useState('');

  const otpValue = otpInputValue.replace(/\s/g, '');
  const isOtpReset = otpValue.length === 0;
  const isOtpComplete = otpValue.length === OTP_LENGTH;
  const isOtpCorrect = isOtpComplete && otpValue === EXPECTED_OTP;
  const isOtpWrong = isOtpComplete && otpValue !== EXPECTED_OTP;
  const isMaxAttemptsReached = failedAttempts >= MAX_OTP_ATTEMPTS;
  const isOneAttemptLeft = failedAttempts === MAX_OTP_ATTEMPTS - 1;

  const handleOtpInput = (value: string[]): void => {
    const nextOtpValue = value.join('');
    setOtpInputValue(nextOtpValue);

    const normalizedOtp = nextOtpValue.replace(/\s/g, '');
    if (normalizedOtp.length === 0) {
      form.setFieldValue('otp', undefined);
      form.setFields([{ name: 'otp', errors: [] }]);
      setLastCheckedOtp('');
    } else if (normalizedOtp.length < OTP_LENGTH) {
      setLastCheckedOtp('');
    }
  };

  const handleResendCode = (): void => {
    setOtpInputValue('');
    form.resetFields(['otp']);
    setOtpRenderKey(previousValue => previousValue + 1);
    setResendResetSignal(previousValue => previousValue + 1);
    setFailedAttempts(0);
    setLastCheckedOtp('');
  };

  let otpValidateStatus: 'success' | 'error' | undefined;

  if (isOtpCorrect) {
    otpValidateStatus = 'success';
  } else if (isOtpWrong) {
    otpValidateStatus = 'error';
  } else {
    otpValidateStatus = undefined;
  }

  let otpHelp: JSX.Element | null = null;

  if (isOtpReset) {
    otpHelp = null;
  } else if (isOtpWrong) {
    otpHelp = <ErrorMessage isOneAttemptLeft={isOneAttemptLeft} />;
  } else if (isOtpCorrect) {
    otpHelp = <SuccessMessage />;
  }

  let otpVisualState: 'error' | 'success' | undefined;

  if (isOtpWrong) {
    otpVisualState = 'error';
  } else if (isOtpCorrect) {
    otpVisualState = 'success';
  }

  useEffect(() => {
    if (isOtpWrong && otpValue !== lastCheckedOtp) {
      setLastCheckedOtp(otpValue);
      setFailedAttempts(previousValue => {
        if (previousValue < MAX_OTP_ATTEMPTS) {
          return previousValue + 1;
        }
        return previousValue;
      });
    }
  }, [isOtpWrong, otpValue, lastCheckedOtp]);

  useEffect(() => {
    if (!isOtpCorrect) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      handleServiceNext();
    }, 1000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isOtpCorrect, handleServiceNext]);

  useEffect(() => {
    if (isMaxAttemptsReached) {
      setOtpInputValue('');
    }
  }, [isMaxAttemptsReached]);

  return (
    <StyledEmailVerificationPage vertical>
      {isMaxAttemptsReached && <MaxAttemptsMessage />}
      <StyledEmailVerificationContent vertical gap={48}>
        <Flex vertical justify="start" align="start" gap={8}>
          <Typography.Title
            level={3}
            style={{ margin: 0, fontWeight: kccThemeConstants.TYPOGRAPHY.FONT_WEIGHT.EXTRA_BOLD }}
          >
            {t('serviceRequestPage.verification.title')}
          </Typography.Title>

          <Typography.Text style={{ margin: 0, fontWeight: kccThemeConstants.TYPOGRAPHY.FONT_WEIGHT.REGULAR }}>
            {t('serviceRequestPage.verification.sentTo', { email: emailRequested })} <br />
            {t('serviceRequestPage.verification.wrongMail')}{' '}
            <Typography.Link onClick={handleServiceBack} underline style={{ color: 'black' }}>
              {t('serviceRequestPage.verification.updateEmail')}
            </Typography.Link>
          </Typography.Text>
        </Flex>

        <Flex vertical gap={16}>
          <Form form={form} autoComplete="off" layout="vertical" style={{ margin: 0 }}>
            <Form.Item name="otp" label={null} validateStatus={otpValidateStatus} help={otpHelp}>
              <StyledOTPWrapper $state={otpVisualState}>
                <Input.OTP
                  key={otpRenderKey}
                  autoFocus
                  length={OTP_LENGTH}
                  value={otpInputValue}
                  onInput={handleOtpInput}
                  disabled={isMaxAttemptsReached}
                  inputMode="numeric"
                />
              </StyledOTPWrapper>
            </Form.Item>
          </Form>
        </Flex>

        <Flex vertical gap={8} className="email-verification__resend-code-countdown">
          <ResendCodeCountdownMemoized
            onResend={handleResendCode}
            resetSignal={resendResetSignal}
            isMaxAttemptsReached={isMaxAttemptsReached}
          />
        </Flex>
      </StyledEmailVerificationContent>
    </StyledEmailVerificationPage>
  );
};
